<template>
  <div class="min-h-screen relative overflow-hidden bg-gray-100">
    <!-- Background using login.png -->
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style="background-image: url('/login.png'); background-color: #e5e7eb;"
    >
      <!-- Overlay to ensure text readability -->
      <div class="absolute inset-0 bg-blue-900/20"></div>
    </div>
    
    <!-- Content Container -->
    <div class="relative z-10 flex min-h-screen items-center justify-center px-6 py-32 lg:px-8">
      <div class="bg-white rounded-2xl max-w-md w-full p-6 border-4 border-black shadow-[8px_8px_0_rgba(0,0,0,1)]">
        
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-8">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg class="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p class="text-gray-600">Loading your trusted devices...</p>
        </div>

        <!-- Main Content -->
        <div v-else>
          <!-- Header -->
          <div class="text-center mb-6">
            <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-red-900 mb-2">Manage Trusted Devices</h1>
          </div>

          <!-- Warning Message (only show when access is actually blocked - 3 devices AND current device not trusted) -->
          <div v-if="isAccessBlocked" class="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
            <div class="flex">
              <svg class="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
              <div class="flex-1">
                <p class="text-xs text-red-700">
                  You must remove at least one trusted device before you can trust this device and access the dashboard.
                </p>
              </div>
            </div>
          </div>

          <!-- Error Message (keep inline for errors) -->
          <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-700">{{ errorMessage }}</p>
          </div>

          <!-- Trusted Devices List -->
          <div class="mb-6 space-y-3">
            <h3 class="text-sm font-semibold text-gray-800 mb-3">Your Trusted Devices:</h3>
            
            <div v-if="trustedDevices.length === 0 && !errorMessage" class="text-center py-8 text-gray-500">
              <p>No trusted devices found. You can now access the dashboard.</p>
            </div>

            <div
              v-for="device in trustedDevices"
              :key="device.id"
              class="p-4 bg-gray-50 border-2 border-gray-300 rounded-lg hover:border-blue-400 transition-colors"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center mb-2">
                    <svg class="h-5 w-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <h4 class="text-sm font-semibold text-gray-900">{{ device.deviceName }}</h4>
                  </div>
                  
                  <div class="text-xs text-gray-600 space-y-1 ml-7">
                    <div>• Browser: {{ device.browserInfo }}</div>
                    <div>• OS: {{ device.osInfo }}</div>
                    <div>• Location: {{ device.location }}</div>
                  </div>

                  <!-- Current Device Badge -->
                  <div v-if="isCurrentDevice(device)" class="mt-2 ml-7">
                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <svg class="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                      This Device
                    </span>
                  </div>
                </div>

                <!-- Remove Button -->
                <button
                  @click="confirmRemoveDevice(device)"
                  :disabled="isRemoving === device.id"
                  class="ml-4 px-3 py-2 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <span v-if="isRemoving !== device.id">Remove</span>
                  <span v-else class="flex items-center">
                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-col space-y-3">
            <button
              v-if="!isAccessBlocked"
              @click="handleContinueToLogin"
              class="w-full px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 cursor-pointer transition-colors"
            >
              {{ isCurrentDeviceTrusted ? 'Continue to Dashboard' : 'Complete Device Trust' }}
            </button>
            <button
              @click="handleLogout"
              class="w-full px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 cursor-pointer transition-colors"
            >
              Logout
            </button>
          </div>

          <!-- Info Notice -->
          <div v-if="isAccessBlocked" class="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="flex">
              <svg class="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
              </svg>
              <p class="text-xs text-blue-700">
                You must remove at least one device before you can trust your current device.
              </p>
            </div>
          </div>
          <div v-else-if="isCurrentDeviceTrusted" class="mt-6 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div class="flex">
              <svg class="h-5 w-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-xs text-green-700">
                Your current device is trusted. You can now proceed to the dashboard.
              </p>
            </div>
          </div>
          <div v-else class="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div class="flex">
              <svg class="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <p class="text-xs text-yellow-700">
                You still need to complete the 2FA device trust process before accessing the dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal with Transitions -->
    <Transition name="modal">
      <div
        v-if="showConfirmModal"
        class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <!-- Background overlay -->
        <Transition name="fade">
          <div
            v-if="showConfirmModal"
            class="fixed inset-0 bg-gray-900/75"
            @click="showConfirmModal = false"
          ></div>
        </Transition>

        <!-- Modal panel -->
        <Transition name="slide-up">
          <div
            v-if="showConfirmModal"
            class="relative z-50 bg-white rounded-lg px-6 pt-5 pb-4 text-left overflow-hidden max-w-lg w-full mx-4 border-2 border-black shadow-[4px_4px_0_rgba(0,0,0,1)]"
          >
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                Remove Trusted Device
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  Are you sure you want to remove <strong>{{ deviceToRemove?.deviceName }}</strong>? 
                  You will need to verify with 2FA the next time you log in from this device.
                </p>
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              @click="handleRemoveDevice"
              type="button"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 cursor-pointer sm:ml-3 sm:w-auto sm:text-sm"
            >
              Remove Device
            </button>
            <button
              @click="showConfirmModal = false"
              type="button"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
          </div>
        </Transition>
      </div>
    </Transition>

    <!-- Toast Notification -->
    <Transition name="toast">
      <div
        v-if="successMessage"
        class="fixed top-4 right-4 z-[60] max-w-sm w-full"
      >
        <div class="bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg border-2 border-green-700 flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <p class="text-sm font-medium">{{ successMessage }}</p>
          </div>
          <button
            @click="successMessage = ''"
            class="ml-4 flex-shrink-0 text-white hover:text-green-100 cursor-pointer"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide up transition for modal */
.slide-up-enter-active {
  transition: all 0.3s ease;
}

.slide-up-leave-active {
  transition: all 0.2s ease;
}

.slide-up-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* Modal wrapper transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Toast notification transition */
.toast-enter-active {
  transition: all 0.3s ease;
}

.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { trustedDeviceService, type TrustedDevice } from '@/services/trustedDeviceService';
import { logDebug, logInfo, logWarn, logError, logSuccess } from '@/utils/secureLogger';
import { createAuditLog } from '@/services/auditLogService';
import { auth } from '@/firebase';

const router = useRouter();
const route = useRoute();

// State
const isLoading = ref(true);
const isRemoving = ref<string | null>(null);
const trustedDevices = ref<TrustedDevice[]>([]);
const successMessage = ref('');
const errorMessage = ref('');
const showConfirmModal = ref(false);
const deviceToRemove = ref<TrustedDevice | null>(null);
const isAccessBlocked = ref(false);

// Get current device info for comparison
const currentDeviceInfo = trustedDeviceService.getDeviceInfo();

// Computed: Check if current device is trusted
const isCurrentDeviceTrusted = computed(() => {
  return trustedDevices.value.some(device => 
    device.deviceFingerprint === currentDeviceInfo.fingerprint
  );
});

// Check if device is the current one
const isCurrentDevice = (device: TrustedDevice) => {
  return device.deviceFingerprint === currentDeviceInfo.fingerprint;
};

// Format date
const formatDate = (timestamp: any) => {
  if (!timestamp) return 'Unknown';
  
  let date;
  if (timestamp.toDate) {
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else {
    date = new Date(timestamp);
  }
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Load trusted devices
const loadTrustedDevices = async () => {
  try {
    isLoading.value = true;
    const devices = await trustedDeviceService.getTrustedDevices();
    trustedDevices.value = devices;
    
    // Check if access is blocked: 3 devices AND current device is NOT trusted
    const currentDeviceTrusted = devices.some(device => 
      device.deviceFingerprint === currentDeviceInfo.fingerprint
    );
    isAccessBlocked.value = devices.length >= 3 && !currentDeviceTrusted;
    
    logSuccess('Loaded trusted devices', { 
      count: devices.length,
      accessBlocked: isAccessBlocked.value 
    });
  } catch (error: any) {
    logError('Error loading trusted devices', error);
    errorMessage.value = error.message || 'Failed to load trusted devices';
  } finally {
    isLoading.value = false;
  }
};

// Show confirmation modal
const confirmRemoveDevice = (device: TrustedDevice) => {
  deviceToRemove.value = device;
  showConfirmModal.value = true;
};

// Remove device
const handleRemoveDevice = async () => {
  if (!deviceToRemove.value) return;

  try {
    isRemoving.value = deviceToRemove.value.id;
    showConfirmModal.value = false;
    errorMessage.value = '';
    successMessage.value = '';

    logDebug('Removing trusted device', { deviceId: deviceToRemove.value.id });

    const result = await trustedDeviceService.removeTrustedDevice(deviceToRemove.value.id);

    if (result.success) {
      logSuccess('Device removed successfully');
      successMessage.value = `Successfully removed ${deviceToRemove.value.deviceName}`;

      // Create audit log
      try {
        await createAuditLog({
          action: 'profile_update',
          type: 'system_event',
          details: `Removed trusted device: ${deviceToRemove.value.deviceName} (forced due to device limit)`,
          performedById: auth.currentUser?.uid,
          performedByName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
          performedByEmail: auth.currentUser?.email || '',
          affectedUserId: auth.currentUser?.uid,
          affectedUserName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
          affectedUserEmail: auth.currentUser?.email || ''
        });
      } catch (auditError) {
        logError('Failed to create audit log', auditError);
      }

      // Reload devices
      await loadTrustedDevices();

      // Auto-dismiss toast after 5 seconds
      setTimeout(() => {
        successMessage.value = '';
      }, 5000);
    } else {
      errorMessage.value = result.message || 'Failed to remove device';
      logError('Failed to remove device', new Error(result.message));
    }
  } catch (error: any) {
    logError('Error removing device', error);
    errorMessage.value = error.message || 'Failed to remove device';
  } finally {
    isRemoving.value = null;
    deviceToRemove.value = null;
  }
};

// Continue to trust device flow (after removing a device)
const handleContinueToLogin = async () => {
  logDebug('User returning to trust device flow after removing device');

  // Check if current device is already trusted
  const currentDeviceTrusted = trustedDevices.value.some(device => 
    device.deviceFingerprint === currentDeviceInfo.fingerprint
  );

  if (currentDeviceTrusted) {
    // Current device is trusted, can proceed to admin
    logDebug('Current device is already trusted, proceeding to admin');
    
    // Create audit log
    try {
      await createAuditLog({
        action: 'admin_auth',
        type: 'system_event',
        details: `Admin proceeding to dashboard after managing devices (${trustedDevices.value.length}/3 devices, current device trusted)`,
        performedById: auth.currentUser?.uid,
        performedByName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
        performedByEmail: auth.currentUser?.email || '',
        affectedUserId: auth.currentUser?.uid,
        affectedUserName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
        affectedUserEmail: auth.currentUser?.email || ''
      });
    } catch (auditError) {
      logError('Failed to create audit log', auditError);
    }

    // Clear pending 2FA session
    sessionStorage.removeItem('pending2FAId');
    
    router.push('/admin');
  } else {
    // Current device NOT trusted, need to complete 2FA trust flow
    logDebug('Current device not trusted, redirecting back to trust device flow');
    
    // Create audit log
    try {
      await createAuditLog({
        action: 'admin_auth',
        type: 'system_event',
        details: `Admin removed device (${trustedDevices.value.length}/3 devices), redirecting to complete 2FA trust flow`,
        performedById: auth.currentUser?.uid,
        performedByName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
        performedByEmail: auth.currentUser?.email || '',
        affectedUserId: auth.currentUser?.uid,
        affectedUserName: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
        affectedUserEmail: auth.currentUser?.email || ''
      });
    } catch (auditError) {
      logError('Failed to create audit log', auditError);
    }

    // Get the 2FA ID from route query or session storage
    const twoFAId = route.query.twoFAId as string || sessionStorage.getItem('pending2FAId');
    
    if (twoFAId) {
      // Redirect back to trust device view to complete the flow
      router.push(`/trust-device?twoFAId=${twoFAId}`);
    } else {
      // No 2FA session, need to start fresh login
      logWarn('No 2FA session found, redirecting to login');
      errorMessage.value = 'Session expired. Please log in again.';
      setTimeout(() => {
        router.push('/login');
      }, 2000);
    }
  }
};

// Handle logout
const handleLogout = async () => {
  logDebug('User logging out from manage devices screen');

  try {
    // Clear pending 2FA session
    sessionStorage.removeItem('pending2FAId');
    
    await auth.signOut();
    logSuccess('User logged out successfully');
    router.push('/login');
  } catch (error: any) {
    logError('Error during logout', error);
    // Clear session storage even on error
    sessionStorage.removeItem('pending2FAId');
    router.push('/login');
  }
};

// Lifecycle
onMounted(async () => {
  // Simple check - if no user after short delay, redirect
  setTimeout(async () => {
    if (!auth.currentUser) {
      logError('No authenticated user');
      router.push('/login');
      return;
    }

    await loadTrustedDevices();
  }, 500);
});
</script>
